import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { db } from '../firebase';
import {
  doc,
  getDoc,
  collection,
  query,
  where,
  getDocs,
  setDoc,
  updateDoc,
  serverTimestamp,
  arrayUnion
} from 'firebase/firestore';
import { recruitmentService } from '../services/recruitmentService';
import { notificationService } from '../services/notificationService';
import { applicationService } from '../services/applicationService';
import {
  getActivityFrequencyLabel,
  getAgeGroupLabel,
  getActivityStyleLabel
} from '../utils/recruitmentLabels';
import { guardContentSubmission, showModerationWarning } from '../utils/moderation';
import { toPlainText } from '../utils/plainText';
import { trackRecruitmentView } from '../utils/analytics';
import { showToast } from '../utils/toast';
import './RecruitmentDetail.css';

const difficultyLevels = {
  beginner: '初心者向け',
  intermediate: '中級者向け',
  advanced: '上級者向け'
};

const PRE_HIKE_CONCERN_OPTIONS = [
  { value: 'fitness', label: '体力不安' },
  { value: 'equipment', label: '装備不足不安' },
  { value: 'navigation', label: '道迷い不安' },
  { value: 'weather', label: '天候不安' },
  { value: 'first_meeting', label: '初対面同行への不安' },
  { value: 'meeting_place', label: '集合場所への不安' },
  { value: 'transport', label: '交通手段への不安' }
];

const POST_HIKE_INITIAL = {
  actualCompleted: '',
  satisfaction: '',
  equipmentShortage: '',
  equipmentShortageCategory: '',
  stopover: '',
  stopoverCategory: '',
  nextMountain: '',
  nextMountainArea: '',
  comment: ''
};

const POST_HIKE_EQUIPMENT_SHORTAGE_OPTIONS = [
  { value: 'clothing', label: 'ウェア・防寒' },
  { value: 'rain_gear', label: '雨具' },
  { value: 'footwear', label: '靴・足まわり' },
  { value: 'food_water', label: '水・行動食' },
  { value: 'navigation', label: '地図・GPS' },
  { value: 'safety', label: 'ヘルメット・救急用品など安全装備' },
  { value: 'snow_gear', label: '雪山・凍結対策装備' },
  { value: 'other', label: 'その他' }
];

const POST_HIKE_STOPOVER_OPTIONS = [
  { value: 'hot_spring', label: '温泉' },
  { value: 'meal', label: '食事' },
  { value: 'shop', label: '買い物' },
  { value: 'station', label: '道の駅・駅' },
  { value: 'sightseeing', label: '観光' },
  { value: 'none', label: '立ち寄りなし' },
  { value: 'other', label: 'その他' }
];

const NEXT_MOUNTAIN_AREA_OPTIONS = [
  { value: 'north_alps', label: '北アルプス' },
  { value: 'central_alps', label: '中央アルプス' },
  { value: 'south_alps', label: '南アルプス' },
  { value: 'yatsugatake', label: '八ヶ岳' },
  { value: 'tanigawa', label: '谷川・上信越' },
  { value: 'okuchichibu', label: '奥秩父' },
  { value: 'kanto', label: '関東近郊' },
  { value: 'kansai', label: '関西近郊' },
  { value: 'kyushu', label: '九州' },
  { value: 'other', label: 'その他' }
];

const TRIP_TYPE_LABELS = {
  day_hike: '日帰り',
  hut_stay: '小屋泊',
  tent_stay: 'テント泊',
  multi_day: '縦走・複数日'
};

const TRANSPORT_LABELS = {
  public_transport: '公共交通',
  car: '車（レンタカー）',
  carpool: '車（相乗り）',
  mixed: '公共交通・車どちらも可'
};

const DANGER_LABELS = {
  chain: '鎖場',
  rock: '岩場',
  snow: '雪渓・残雪',
  river_crossing: '渡渉',
  steep_descent: '急登・急下降'
};

const EQUIPMENT_REQUIREMENT_LABELS = {
  helmet: 'ヘルメット',
  chain_spike: 'チェーンスパイク',
  trekking_pole: 'トレッキングポール',
  crampon: 'アイゼン',
  first_aid: 'ファーストエイドキット',
  hardshell: 'ハードシェル',
  avalanche_gear: '雪崩装備',
  rainwear: 'レインウェア',
  headlamp: 'ヘッドライト'
};

const MEETING_AREA_LABELS = {
  station: '駅集合',
  trailhead: '登山口集合',
  local: '現地集合',
  other: 'その他'
};

const EXPERIENCE_LEVEL_TARGET_LABELS = {
  beginner_welcome: '初心者歓迎',
  beginner: '初級者向け',
  intermediate: '中級者向け',
  advanced: '上級者向け'
};

const CAR_SHARE_LABELS = {
  available: '相乗り可',
  not_available: '相乗り不可',
  consult: '相談可'
};

const WEATHER_THRESHOLD_LABELS = {
  light_rain_ok: '小雨決行',
  rain_cancel: '雨天中止',
  storm_cancel: '荒天中止',
  decide_previous_day: '前日判断'
};

const ESTIMATED_BUDGET_LABELS = {
  under_3000: '3,000円未満',
  '3000_5000': '3,000〜5,000円',
  '5000_10000': '5,000〜10,000円',
  '10000_20000': '10,000〜20,000円',
  over_20000: '20,000円以上'
};

const REPORT_REASON_OPTIONS = [
  { value: 'violence_threat', label: '暴力・脅迫' },
  { value: 'romance_solicitation', label: 'ナンパ・恋愛目的の勧誘' },
  { value: 'dangerous_hiking_plan', label: '危険な登山計画・行動' },
  { value: 'last_minute_cancel_or_nuisance', label: 'ドタキャン・迷惑行為' },
  { value: 'impersonation_false_info', label: 'なりすまし・虚偽情報' },
  { value: 'personal_info_abuse', label: '個人情報悪用' },
  { value: 'other', label: 'その他' }
];

const REPORT_RECEIVED_MESSAGE = '通報を受け付けました。通報内容を確認してから、原則24時間以内に一次判断、最大48時間以内に確定判断で対応します。開発者X（https://x.com/yamaloglist）でもDM頂けますと状況確認が早くできますので、ご協力お願いいたします。';
const GENDER_MATCH_APPLICATION_ERROR = 'この募集はプロフィール性別が一致する人のみ応募可能です';

const isBeginnerDangerous = (recruitment) => {
  return recruitment?.difficulty === 'advanced'
    || (Array.isArray(recruitment?.dangerFlags) && recruitment.dangerFlags.length > 0);
};

function RecruitmentDetail() {
  const { id } = useParams();
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [recruitment, setRecruitment] = useState(null);
  const [organizer, setOrganizer] = useState(null);
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [hasApplied, setHasApplied] = useState(false);
  const [myApplication, setMyApplication] = useState(null);
  const [reviewRating, setReviewRating] = useState('good');
  const [reviewComment, setReviewComment] = useState('');
  const [showReviewForm, setShowReviewForm] = useState(false);
  const [reviewTarget, setReviewTarget] = useState(null); // {userId, nickname}
  const [preHikeSurvey, setPreHikeSurvey] = useState({
    concerns: [],
    insuranceStatus: '',
    comment: ''
  });
  const [postHikeSurvey, setPostHikeSurvey] = useState(POST_HIKE_INITIAL);
  const [showReportForm, setShowReportForm] = useState(false);
  const [reportReason, setReportReason] = useState('');
  const [reportComment, setReportComment] = useState('');
  const [reviewedTargetIds, setReviewedTargetIds] = useState(new Set());
  const [submitting, setSubmitting] = useState(false);
  const [myGender, setMyGender] = useState('');
  const [approvingIds, setApprovingIds] = useState(new Set());
  const [showSharePrompt, setShowSharePrompt] = useState(
    Boolean(location.state?.showSharePrompt) ||
    new URLSearchParams(location.search).get('share') === '1'
  );

  const getApplicantDisplayName = (application) => {
    return (
      application?.applicant?.nickname ||
      application?.applicant?.displayName ||
      '不明'
    );
  };

  const buildReviewId = (reviewerId, recruitmentId, revieweeId) =>
    `${reviewerId}_${recruitmentId}_${revieweeId}`;

  const shareUrl = `${window.location.origin}${process.env.PUBLIC_URL || ''}/recruitments/${id}`;
  const shareTitle = recruitment?.mountainName || recruitment?.groupName || '募集';
  const shareText = `${shareTitle} の募集を作成しました。参加メンバー募集中です。`;
  const twitterShareUrl = `https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}&url=${encodeURIComponent(shareUrl)}`;
  const lineShareUrl = `https://social-plugins.line.me/lineit/share?url=${encodeURIComponent(shareUrl)}`;

  const markTargetReviewed = useCallback((revieweeId) => {
    setReviewedTargetIds(prev => {
      const next = new Set(prev);
      next.add(revieweeId);
      return next;
    });
  }, []);

  const handlePreHikeConcernChange = (value) => {
    setPreHikeSurvey(prev => {
      const concerns = prev.concerns.includes(value)
        ? prev.concerns.filter(item => item !== value)
        : [...prev.concerns, value];
      return { ...prev, concerns };
    });
  };

  const handlePostHikeSurveyChange = (field, value) => {
    setPostHikeSurvey(prev => ({
      ...prev,
      [field]: value
    }));
  };

  useEffect(() => {
    trackRecruitmentView(id, window.location.pathname).catch((error) => {
      console.error('募集詳細閲覧の計測に失敗しました:', error);
    });
  }, [id]);

  const fetchRecruitmentDetail = useCallback(async () => {
    if (!currentUser) {
      navigate('/login', {
        replace: true,
        state: {
          message: 'ログインすると詳細が見れます。',
          redirectTo: `/recruitments/${id}`,
        },
      });
      return;
    }

    try {
      // 募集情報を取得
      const recruitmentDoc = await getDoc(doc(db, 'recruitments', id));
      if (!recruitmentDoc.exists()) {
        navigate('/recruitments');
        return;
      }

      const recruitmentData = { id: recruitmentDoc.id, ...recruitmentDoc.data() };

      // 通報による非表示チェック: 主催者以外はアクセス不可
      if (recruitmentData.isHidden && recruitmentData.userId !== currentUser?.uid) {
        showToast({ message: 'この募集は現在表示できません。', type: 'error' });
        navigate('/recruitments');
        return;
      }

      // 単発募集の自動クローズ: 出発日を過ぎて status が active のまま放置されている場合
      if (
        recruitmentData.recruitmentType === 'single' &&
        recruitmentData.status === 'active' &&
        recruitmentData.departureDate &&
        currentUser
      ) {
        const depDate = recruitmentData.departureDate.toDate
          ? recruitmentData.departureDate.toDate()
          : new Date(recruitmentData.departureDate);
        if (depDate < new Date()) {
          try {
            await updateDoc(doc(db, 'recruitments', id), {
              status: 'closed',
              updatedAt: serverTimestamp(),
            });
            recruitmentData.status = 'closed';
          } catch (e) {
            console.warn('Auto-close failed:', e);
          }
        }
      }

      setRecruitment(recruitmentData);

      // 募集者情報は埋め込みデータを使用（N+1問題解消）
      if (recruitmentData.organizer) {
        setOrganizer(recruitmentData.organizer);
      } else {
        // フォールバック：埋め込みデータがない場合のみ個別取得
        const organizerDoc = await getDoc(doc(db, 'users', recruitmentData.userId));
        if (organizerDoc.exists()) {
          setOrganizer(organizerDoc.data());
        }
      }

      // 応募者情報を取得（N+1問題解消版）
      const applicationsQuery = query(
        collection(db, 'applications'),
        where('recruitmentId', '==', id)
      );
      const applicationsSnapshot = await getDocs(applicationsQuery);
      
      // 応募者情報は埋め込みデータを使用（ループ内でのgetDocを廃止）
      const applicationsData = applicationsSnapshot.docs.map(appSnapshot => ({
        id: appSnapshot.id,
        ...appSnapshot.data()
      }));
      
      setApplications(applicationsData);

      // 現在のユーザーが既に応募しているかチェック
      if (currentUser) {
        const userApplication = applicationsData.find(
          app => app.userId === currentUser.uid
        );
        setHasApplied(!!userApplication);
        setMyApplication(userApplication || null);

        if (recruitmentData.recruitmentType === 'single' && recruitmentData.status === 'closed') {
          const existingReviewsSnapshot = await getDocs(
            query(
              collection(db, 'reviews'),
              where('reviewerId', '==', currentUser.uid),
              where('recruitmentId', '==', id)
            )
          );
          const reviewedIds = existingReviewsSnapshot.docs
            .map(reviewDoc => reviewDoc.data().revieweeId)
            .filter(Boolean);
          setReviewedTargetIds(new Set(reviewedIds));
        } else {
          setReviewedTargetIds(new Set());
        }
      } else {
        setHasApplied(false);
        setMyApplication(null);
        setReviewedTargetIds(new Set());
      }

    } catch (error) {
      console.error('募集詳細の取得に失敗しました:', error);
      navigate('/recruitments');
    } finally {
      setLoading(false);
    }
  }, [id, currentUser, navigate]);

  useEffect(() => {
    fetchRecruitmentDetail();
  }, [fetchRecruitmentDetail]);

  useEffect(() => {
    if (!currentUser) return;
    getDoc(doc(db, 'users', currentUser.uid)).then(snap => {
      if (snap.exists()) setMyGender(snap.data().gender || '');
    }).catch(() => {});
  }, [currentUser]);

  useEffect(() => {
    const shouldResetUrl =
      Boolean(location.state?.showSharePrompt) ||
      new URLSearchParams(location.search).get('share') === '1';

    if (!shouldResetUrl) return;
    navigate(location.pathname, { replace: true });
  }, [location.pathname, location.search, location.state, navigate]);

  const handleNativeShare = async () => {
    try {
      if (navigator.share) {
        await navigator.share({
          title: shareTitle,
          text: shareText,
          url: shareUrl,
        });
        return;
      }

      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(shareUrl);
        showToast({ message: '募集URLをコピーしました。', type: 'success' });
        return;
      }

      window.open(shareUrl, '_blank', 'noopener,noreferrer');
    } catch (shareError) {
      console.error('Share failed:', shareError);
    }
  };

  const handleApply = async () => {
    if (!currentUser) {
      showToast({ message: '応募するにはログインが必要です。', type: 'info' });
      navigate('/login');
      return;
    }

    if (hasApplied) {
      showToast({ message: '既に応募しています。', type: 'info' });
      return;
    }

    const isGroup = recruitment.recruitmentType === 'group';
    const approvedCount = recruitment.approvedCount || 0;
    if (!isGroup && recruitment.maxParticipants > 0 && approvedCount >= recruitment.maxParticipants) {
      showToast({ message: '募集人数に達しています。', type: 'info' });
      return;
    }

    // 性別制限チェック（women_only / men_only）
    setSubmitting(true);
    try {
      const condition = recruitment.participantCondition;
      if (!isGroup && (condition === 'women_only' || condition === 'men_only')) {
        const profileSnap = await getDoc(doc(db, 'users', currentUser.uid));
        const gender = profileSnap.exists() ? profileSnap.data().gender : '';
        const allowed = condition === 'women_only' ? gender === '女性' : gender === '男性';
        if (!allowed) {
          showToast({ message: 'この募集はプロフィール性別が一致する人のみ応募可能です。', type: 'error' });
          return;
        }
      }

      await applicationService.create(id, {
        concerns: preHikeSurvey.concerns,
        insuranceStatus: preHikeSurvey.insuranceStatus,
        comment: toPlainText(preHikeSurvey.comment).slice(0, 500)
      });

      showToast({ message: '応募しました！', type: 'success' });
      setPreHikeSurvey({
        concerns: [],
        insuranceStatus: '',
        comment: ''
      });
      await fetchRecruitmentDetail();

    } catch (error) {
      console.error('応募に失敗しました:', error);

      // ユーザーフレンドリーなエラーメッセージ
      if (error.message?.includes(GENDER_MATCH_APPLICATION_ERROR)) {
        showToast({ message: GENDER_MATCH_APPLICATION_ERROR, type: 'error' });
      } else if (error.message === '定員に達しました') {
        showToast({ message: '募集人数に達しました。', type: 'error' });
      } else if (error.message === '募集が存在しません') {
        showToast({ message: '募集が見つかりません。', type: 'error' });
      } else {
        showToast({ message: '応募に失敗しました。もう一度お試しください。', type: 'error' });
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancelApplication = async () => {
    if (!currentUser) {
      showToast({ message: 'ログインが必要です。', type: 'info' });
      return;
    }

    setSubmitting(true);
    try {
      await applicationService.cancel(`${id}_${currentUser.uid}`);

      showToast({ message: '応募をキャンセルしました！', type: 'success' });
      fetchRecruitmentDetail(); // UX改善：リロードせずにデータ再取得

    } catch (error) {
      console.error('応募キャンセルに失敗しました:', error);

      // ユーザーフレンドリーなエラーメッセージ
      if (error.message === '応募が見つかりません') {
        showToast({ message: '応募が見つかりません。', type: 'error' });
      } else if (error.message === '募集が存在しません') {
        showToast({ message: '募集が見つかりません。', type: 'error' });
      } else {
        showToast({ message: '応募キャンセルに失敗しました。もう一度お試しください。', type: 'error' });
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleStartChat = async (otherUserId, otherUserName) => {
    const chatId = [currentUser.uid, otherUserId].sort().join('_') + '_' + id;
    const chatRef = doc(db, 'chats', chatId);
    const chatDoc = await getDoc(chatRef);
    if (!chatDoc.exists()) {
      // Firestoreからニックネームを取得（アカウント名でなくニックネーム優先）
      const myProfileDoc = await getDoc(doc(db, 'users', currentUser.uid));
      const myNickname = myProfileDoc.exists()
        ? (myProfileDoc.data().nickname || currentUser.displayName || '名無しの登山者')
        : (currentUser.displayName || '名無しの登山者');

      // 主催者IDを特定（どちらが主催者か）
      const organizerId = recruitment.userId;
      // 相手のニックネームをFirestoreから取得（最新を優先）
      let resolvedOtherName = otherUserName || '名無しの登山者';
      try {
        const otherProfileDoc = await getDoc(doc(db, 'users', otherUserId));
        if (otherProfileDoc.exists()) {
          const pd = otherProfileDoc.data();
          resolvedOtherName = pd.nickname || pd.displayName || resolvedOtherName;
        }
      } catch (_) {}

      await setDoc(chatRef, {
        participants: [currentUser.uid, otherUserId],
        organizerId,
        recruitmentId: id,
        recruitmentName: recruitment.mountainName || recruitment.groupName || '',
        participantNames: {
          [currentUser.uid]: myNickname,
          [otherUserId]: resolvedOtherName
        },
        lastMessage: '',
        lastMessageAt: serverTimestamp(),
        createdAt: serverTimestamp()
      });
    }
    navigate(`/chat/${chatId}`);
  };

  const handleDeleteRecruitment = async () => {
    if (!window.confirm('この募集を削除しますか？\n※関連する応募データもすべて削除されます。')) {
      return;
    }
    setSubmitting(true);
    try {
      await recruitmentService.deleteRecruitment(id);
      showToast({ message: '募集を削除しました。', type: 'success' });
      navigate('/recruitments');
    } catch (err) {
      console.error('募集の削除に失敗しました:', err);
      showToast({ message: '削除に失敗しました。もう一度お試しください。', type: 'error' });
    } finally {
      setSubmitting(false);
    }
  };

  const handleApproveApplication = async (applicationId) => {
    if (approvingIds.has(applicationId)) return;
    setApprovingIds(prev => new Set(prev).add(applicationId));
    setSubmitting(true);

    // オプティミスティックUI更新
    setApplications(prev => prev.map(a =>
      a.id === applicationId ? { ...a, applicationStatus: 'approved' } : a
    ));

    try {
      const targetApplication = applications.find(a => a.id === applicationId);
      const wasApproved = targetApplication?.applicationStatus === 'approved';
      const applicantUid = targetApplication?.userId;

      await applicationService.approve(applicationId);

      // グループチャットが既に存在する場合は参加者を自動追加
      if (!wasApproved && applicantUid) {
        const groupChatId = `group_${id}`;
        const groupChatRef = doc(db, 'chats', groupChatId);
        const groupChatSnap = await getDoc(groupChatRef);
        if (groupChatSnap.exists()) {
          let nickname = '名無しの登山者';
          try {
            const userDoc = await getDoc(doc(db, 'users', applicantUid));
            if (userDoc.exists()) {
              const d = userDoc.data();
              nickname = d.nickname || d.displayName || nickname;
            }
          } catch (_) {}
          await updateDoc(groupChatRef, {
            participants: arrayUnion(applicantUid),
            [`participantNames.${applicantUid}`]: nickname,
            updatedAt: serverTimestamp()
          });
        }
      }

      fetchRecruitmentDetail();
    } catch (err) {
      console.error('承認に失敗しました:', err);
      // ロールバック
      setApplications(prev => prev.map(a =>
        a.id === applicationId ? { ...a, applicationStatus: 'pending' } : a
      ));
      showToast({ message: '承認に失敗しました。', type: 'error' });
    } finally {
      setSubmitting(false);
      setApprovingIds(prev => { const s = new Set(prev); s.delete(applicationId); return s; });
    }
  };

  // グループチャット開始（主催者のみ）
  const handleStartGroupChat = async () => {
    const approvedApps = applications.filter(a => a.applicationStatus === 'approved');
    if (approvedApps.length === 0) {
      showToast({ message: '承認済みの応募者がいません。先に応募を承認してください。', type: 'info' });
      return;
    }

    const groupChatId = `group_${id}`;
    const groupChatRef = doc(db, 'chats', groupChatId);

    try {
      const existingSnap = await getDoc(groupChatRef);
      if (!existingSnap.exists()) {
        // 主催者のニックネーム取得
        const myProfileDoc = await getDoc(doc(db, 'users', currentUser.uid));
        const myNickname = myProfileDoc.exists()
          ? (myProfileDoc.data().nickname || currentUser.displayName || '名無しの登山者')
          : (currentUser.displayName || '名無しの登山者');

        // 承認済み応募者のニックネームを取得
        const participantNames = { [currentUser.uid]: myNickname };
        const participantUids = [currentUser.uid];

        await Promise.all(approvedApps.map(async app => {
          const uid = app.userId;
          participantUids.push(uid);
          try {
            const userDoc = await getDoc(doc(db, 'users', uid));
            if (userDoc.exists()) {
              const d = userDoc.data();
              participantNames[uid] = d.nickname || d.displayName || '名無しの登山者';
            } else {
              participantNames[uid] = app.applicant?.nickname || '名無しの登山者';
            }
          } catch (_) {
            participantNames[uid] = app.applicant?.nickname || '名無しの登山者';
          }
        }));

        await setDoc(groupChatRef, {
          type: 'group',
          recruitmentId: id,
          recruitmentName: recruitment.mountainName || recruitment.groupName || '',
          organizerId: currentUser.uid,
          participants: participantUids,
          participantNames,
          lastMessage: '',
          lastMessageAt: serverTimestamp(),
          createdAt: serverTimestamp()
        });
      }
      navigate(`/group-chat/${id}`);
    } catch (err) {
      console.error('グループチャット開始に失敗しました:', err);
      showToast({ message: 'グループチャットの開始に失敗しました。', type: 'error' });
    }
  };

  const handleRejectApplication = async (applicationId) => {
    if (!window.confirm('この応募を拒否しますか？')) return;
    setSubmitting(true);
    try {
      await applicationService.reject(applicationId);

      fetchRecruitmentDetail();
    } catch (err) {
      console.error('拒否に失敗しました:', err);
      showToast({ message: '拒否に失敗しました。', type: 'error' });
    } finally {
      setSubmitting(false);
    }
  };

  const handleUnapproveApplication = async (applicationId) => {
    if (!window.confirm('承認を解除して「承認待ち」に戻しますか？')) return;
    setSubmitting(true);
    try {
      await applicationService.unApprove(applicationId);
      fetchRecruitmentDetail();
    } catch (err) {
      console.error('承認解除に失敗しました:', err);
      showToast({ message: '承認解除に失敗しました。', type: 'error' });
    } finally {
      setSubmitting(false);
    }
  };

  const handleUnrejectApplication = async (applicationId) => {
    if (!window.confirm('拒否を解除して「承認待ち」に戻しますか？')) return;
    setSubmitting(true);
    try {
      await applicationService.unReject(applicationId);
      fetchRecruitmentDetail();
    } catch (err) {
      console.error('拒否解除に失敗しました:', err);
      showToast({ message: '拒否解除に失敗しました。', type: 'error' });
    } finally {
      setSubmitting(false);
    }
  };

  const handleReport = async () => {
    if (!reportReason) {
      showToast({ message: '通報理由を選択してください。', type: 'error' });
      return;
    }
    try {
      const { addDoc, collection: col } = await import('firebase/firestore');
      await addDoc(col(db, 'reports'), {
        type: 'recruitment',
        targetId: id,
        targetName: recruitment.mountainName,
        reporterId: currentUser.uid,
        reason: reportReason,
        comment: reportComment,
        decisionStatus: 'pending',
        reviewedBy: '',
        reviewedAt: null,
        actionTaken: 'none',
        createdAt: serverTimestamp()
      });
      await notificationService.create(currentUser.uid, 'report_received', {
        message: REPORT_RECEIVED_MESSAGE,
        targetType: 'recruitment',
        targetId: id,
        targetName: recruitment.mountainName || ''
      });
      showToast({ message: '通報を受け付けました。運営が確認します。', type: 'success' });
      setShowReportForm(false);
      setReportReason('');
      setReportComment('');
    } catch (err) {
      console.error('通報に失敗しました:', err);
      showToast({ message: '通報に失敗しました。', type: 'error' });
    }
  };

  const handleSubmitReview = async () => {
    if (!reviewTarget) return;
    setSubmitting(true);
    try {
      // 二重評価防止: reviewerId_recruitmentId の決定論的IDを使用
      // Security Rules の allow update: if false と組み合わせてサーバー側でも防止
      const moderation = await guardContentSubmission({
        action: 'review_create',
        contentType: 'review',
        texts: [reviewComment],
        metadata: {
          recruitmentId: id,
          revieweeId: reviewTarget.userId,
        },
      });
      showModerationWarning(moderation);

      const reviewId = buildReviewId(currentUser.uid, id, reviewTarget.userId);
      const reviewRef = doc(db, 'reviews', reviewId);
      const existingSnap = await getDoc(reviewRef);
      if (existingSnap.exists()) {
        showToast({ message: 'この山行にはすでに評価を送信しています。', type: 'info' });
        setShowReviewForm(false);
        markTargetReviewed(reviewTarget.userId);
        return;
      }

      await setDoc(reviewRef, {
        reviewerId: currentUser.uid,
        revieweeId: reviewTarget.userId,
        recruitmentId: id,
        rating: reviewRating,
        comment: reviewComment,
        postHikeSurvey: {
          actualCompleted: postHikeSurvey.actualCompleted,
          satisfaction: postHikeSurvey.satisfaction ? Number(postHikeSurvey.satisfaction) : null,
          equipmentShortage: postHikeSurvey.equipmentShortage,
          equipmentShortageCategory: postHikeSurvey.equipmentShortage === 'yes' ? postHikeSurvey.equipmentShortageCategory : '',
          stopover: toPlainText(postHikeSurvey.stopover).slice(0, 200),
          stopoverCategory: postHikeSurvey.stopoverCategory,
          nextMountain: toPlainText(postHikeSurvey.nextMountain).slice(0, 100),
          nextMountainArea: postHikeSurvey.nextMountainArea,
          comment: toPlainText(postHikeSurvey.comment).slice(0, 500)
        },
        createdAt: serverTimestamp()
      });
      showToast({ message: '評価を送信しました。', type: 'success' });
      markTargetReviewed(reviewTarget.userId);
      setShowReviewForm(false);
      setReviewTarget(null);
      setReviewComment('');
      setPostHikeSurvey(POST_HIKE_INITIAL);
    } catch (err) {
      console.error('評価の送信に失敗しました:', err);
      showToast({ message: '評価の送信に失敗しました。', type: 'error' });
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return <div className="loading">読み込み中...</div>;
  }

  if (!recruitment) {
    return <div className="error">募集が見つかりませんでした。</div>;
  }

  const isOrganizer = currentUser && currentUser.uid === recruitment.userId;
  const approvedCount = recruitment.approvedCount || applications.filter(
    (application) => application.applicationStatus === 'approved'
  ).length;
  const isGroupType = recruitment.recruitmentType === 'group';
  const isFull = !isGroupType && recruitment.maxParticipants > 0 && approvedCount >= recruitment.maxParticipants;

  const GENDER_RATIO_LABELS = {
    male_majority: '男性が多い', female_majority: '女性が多い',
    balanced: 'バランス良好', all_male: '男性のみ', all_female: '女性のみ'
  };

  return (
    <div className="recruitment-detail-container">
      {submitting && (
        <div className="loading-overlay">
          <div className="loading-spinner" />
          処理中...
        </div>
      )}
      <div className="detail-header">
        <Link to="/recruitments" className="back-link">
          ← 募集一覧に戻る
        </Link>
      </div>

      <div className="detail-content">
        <div className="main-info">
          <div className="card">
            <div className="card-header">
              <h1>{isGroupType ? recruitment.groupName : recruitment.mountainName}</h1>
              {!isGroupType && recruitment.difficulty && (
                <span className={`difficulty ${recruitment.difficulty}`}>
                  {difficultyLevels[recruitment.difficulty]}
                </span>
              )}
            </div>

            <div className="card-body">
              {recruitment.imageUrl && (
                <div style={{marginBottom:'16px'}}>
                  <img src={recruitment.imageUrl} alt="募集画像" style={{width:'100%', maxHeight:'300px', objectFit:'cover', borderRadius:'8px'}} />
                </div>
              )}

              {recruitment.recruitmentType === 'single' && recruitment.eventName && (
                <div className="info-item" style={{marginBottom:'12px'}}>
                  <label>イベント名</label>
                  <p>{recruitment.eventName}</p>
                </div>
              )}

              {!isGroupType && (
                <div className="safety-notice" style={{ marginBottom: '16px' }}>
                  <strong>安全確認</strong>
                  <p style={{ margin: '6px 0 0' }}>
                    募集者はこの山行の計画責任者です。登山は天候急変、滑落、道迷い、装備不足などの危険を伴うため、参加可否は各自の責任で判断してください。
                  </p>
                  <p style={{ margin: '6px 0 0' }}>
                    登山計画書を忘れずに提出してください。必要装備、標準CT、撤退判断も事前に確認してください。
                  </p>
                  {isBeginnerDangerous(recruitment) && (
                    <p style={{ color: 'var(--ember)', fontWeight: 700, margin: '8px 0 0' }}>
                      この山は初心者には危険です。経験・装備・天候条件を十分に確認してください。
                    </p>
                  )}
                </div>
              )}

              <div className="info-grid">
                {/* 単発募集のみ */}
                {!isGroupType && recruitment.route && (
                  <div className="info-item">
                    <label>登山ルート</label>
                    <p>{recruitment.route}</p>
                  </div>
                )}

                {!isGroupType && recruitment.departureDate && (
                  <div className="info-item">
                    <label>出発予定日</label>
                    <p>{recruitment.departureDate?.toDate
                      ? recruitment.departureDate.toDate().toLocaleDateString('ja-JP', { year: 'numeric', month: 'long', day: 'numeric', weekday: 'long' })
                      : new Date(recruitment.departureDate).toLocaleDateString('ja-JP', { year: 'numeric', month: 'long', day: 'numeric', weekday: 'long' })
                    }</p>
                  </div>
                )}

                <div className="info-item">
                  <p className="participants">
                    {isGroupType ? (
                      <>👥 応募数: {approvedCount}件</>
                    ) : recruitment.maxParticipants > 0 ? (
                      <>👥 {approvedCount}/{recruitment.maxParticipants}人</>
                    ) : (
                      <>👥 応募数: {approvedCount}件</>
                    )}
                  </p>
                </div>

                {recruitment.prefecture && (
                  <div className="info-item">
                    <label>{isGroupType ? '活動地域' : '出発地域'}</label>
                    <p>{recruitment.prefecture}</p>
                  </div>
                )}

                {!isGroupType && (
                  <div className="info-item">
                    <label>集合場所・時間</label>
                    <p>{recruitment.meetingPoint || '未定'}</p>
                  </div>
                )}

                {!isGroupType && recruitment.carpoolOption && recruitment.carpoolOption !== 'none' && (
                  <div className="info-item">
                    <label>相乗り</label>
                    <p>
                      {recruitment.carpoolOption === 'driver' && '🚗 車出しできます（ドライバー）'}
                      {recruitment.carpoolOption === 'passenger' && '🙋 車に乗せてほしい'}
                      {recruitment.carpoolOption === 'either' && '🚗 ドライバー・同乗どちらでも可'}
                    </p>
                  </div>
                )}

                {!isGroupType && recruitment.tripType && (
                  <div className="info-item">
                    <label>山行タイプ</label>
                    <p>{TRIP_TYPE_LABELS[recruitment.tripType] || recruitment.tripType}</p>
                  </div>
                )}

                {!isGroupType && recruitment.transportMode && (
                  <div className="info-item">
                    <label>主な移動手段</label>
                    <p>{TRANSPORT_LABELS[recruitment.transportMode] || recruitment.transportMode}</p>
                  </div>
                )}

                {!isGroupType && recruitment.meetingAreaType && (
                  <div className="info-item">
                    <label>集合場所タイプ</label>
                    <p>{MEETING_AREA_LABELS[recruitment.meetingAreaType] || recruitment.meetingAreaType}</p>
                  </div>
                )}

                {!isGroupType && recruitment.experienceLevelTarget && (
                  <div className="info-item">
                    <label>対象経験レベル</label>
                    <p>{EXPERIENCE_LEVEL_TARGET_LABELS[recruitment.experienceLevelTarget] || recruitment.experienceLevelTarget}</p>
                  </div>
                )}

                {!isGroupType && recruitment.carShareAvailable && (
                  <div className="info-item">
                    <label>相乗り可否</label>
                    <p>{CAR_SHARE_LABELS[recruitment.carShareAvailable] || recruitment.carShareAvailable}</p>
                  </div>
                )}

                {!isGroupType && recruitment.weatherThreshold && (
                  <div className="info-item">
                    <label>天候判断</label>
                    <p>{WEATHER_THRESHOLD_LABELS[recruitment.weatherThreshold] || recruitment.weatherThreshold}</p>
                  </div>
                )}

                {!isGroupType && recruitment.estimatedBudgetBand && (
                  <div className="info-item">
                    <label>想定予算</label>
                    <p>{ESTIMATED_BUDGET_LABELS[recruitment.estimatedBudgetBand] || recruitment.estimatedBudgetBand}</p>
                  </div>
                )}

                {!isGroupType && recruitment.walkingHours && (
                  <div className="info-item">
                    <label>標準CT</label>
                    <p>{recruitment.walkingHours}時間</p>
                  </div>
                )}

                {!isGroupType && recruitment.elevationGain && (
                  <div className="info-item">
                    <label>標高差目安</label>
                    <p>{recruitment.elevationGain}m</p>
                  </div>
                )}

                {!isGroupType && Array.isArray(recruitment.dangerFlags) && recruitment.dangerFlags.length > 0 && (
                  <div className="info-item">
                    <label>危険箇所・注意地形</label>
                    <p>{recruitment.dangerFlags.map(value => DANGER_LABELS[value] || value).join('、')}</p>
                  </div>
                )}

                {!isGroupType && Array.isArray(recruitment.equipmentRequirements) && recruitment.equipmentRequirements.length > 0 && (
                  <div className="info-item">
                    <label>装備条件</label>
                    <p>{recruitment.equipmentRequirements.map(value => EQUIPMENT_REQUIREMENT_LABELS[value] || value).join('、')}</p>
                  </div>
                )}

                <div className="info-item">
                  <label>募集状況</label>
                  <p className={`status ${recruitment.status}`}>
                    {recruitment.status === 'active' ? '募集中' :
                     recruitment.status === 'closed' ? '募集終了' : '中止'}
                  </p>
                </div>

                {!isGroupType && recruitment.minParticipants && (
                  <div className="info-item">
                    <label>最低成立人数</label>
                    <p>{recruitment.minParticipants}人</p>
                  </div>
                )}
                {!isGroupType && recruitment.genderRatioSingle && (
                  <div className="info-item">
                    <label>男女比</label>
                    <p>{recruitment.genderRatioSingle}</p>
                  </div>
                )}
                {recruitment.participantCondition && (
                  <div className="info-item">
                    <label>参加条件</label>
                    <p>{{
                      anyone: '誰でも歓迎',
                      interview: '事前面談あり',
                      experienced: '登山経験者のみ',
                      women_only: '女性限定',
                      men_only: '男性限定'
                    }[recruitment.participantCondition] || recruitment.participantCondition}</p>
                  </div>
                )}

                {/* 団体募集固有の情報 */}
                {isGroupType && recruitment.activityFrequency && (
                  <div className="info-item">
                    <label>活動頻度</label>
                    <p>{getActivityFrequencyLabel(recruitment.activityFrequency, recruitment.activityFrequencyOther)}</p>
                  </div>
                )}
                {isGroupType && recruitment.currentMembers && (
                  <div className="info-item">
                    <label>現在のメンバー数</label>
                    <p>{recruitment.currentMembers}人</p>
                  </div>
                )}
                {isGroupType && recruitment.genderRatio && (
                  <div className="info-item">
                    <label>男女比</label>
                    <p>{GENDER_RATIO_LABELS[recruitment.genderRatio] || recruitment.genderRatio}</p>
                  </div>
                )}
                {isGroupType && recruitment.ageGroup && (
                  <div className="info-item">
                    <label>年齢層</label>
                    <p>{getAgeGroupLabel(recruitment.ageGroup)}</p>
                  </div>
                )}
                {isGroupType && recruitment.activityStyle && (
                  <div className="info-item">
                    <label>活動スタイル</label>
                    <p>{getActivityStyleLabel(recruitment.activityStyle)}</p>
                  </div>
                )}
                {isGroupType && recruitment.homepage && (
                  <div className="info-item">
                    <label>HP・SNS</label>
                    <p><a href={recruitment.homepage} target="_blank" rel="noopener noreferrer" style={{ color: 'var(--alpine)' }}>{recruitment.homepage}</a></p>
                  </div>
                )}
              </div>

              <div className="description-section">
                <label>詳細説明</label>
                <p className="description">{toPlainText(recruitment.description)}</p>
              </div>

              {(recruitment.equipment || !isGroupType) && (
                <div className="equipment-section">
                  <label>持ち物・注意事項</label>
                  <p className="equipment">{toPlainText(recruitment.equipment) || '特になし'}</p>
                </div>
              )}

              {!isGroupType && (
                <div className="rental-link-section">
                  <label>装備サポート</label>
                  <div className="rental-links">
                    <a href="https://yamano-media.com/blog/yamadogu/" target="_blank" rel="noopener noreferrer" className="btn btn-outline">
                      装備レンタル
                    </a>
                    <a href="https://yamano-media.com/blog/mountain-checker-app/" target="_blank" rel="noopener noreferrer" className="btn btn-outline">
                      装備チェックリスト
                    </a>
                  </div>
                </div>
              )}
            </div>

            <div className="card-footer">
              {showSharePrompt && isOrganizer && (
                <div className="share-prompt-card">
                  <div className="share-prompt-card__header">
                    <div>
                      <p className="share-prompt-card__eyebrow">Share</p>
                      <h3>募集作成した後は X(Twitter) で共有してみよう</h3>
                    </div>
                    <button
                      type="button"
                      className="share-prompt-card__close"
                      onClick={() => setShowSharePrompt(false)}
                      aria-label="共有案内を閉じる"
                    >
                      ×
                    </button>
                  </div>
                  <div className="share-prompt-card__actions">
                    <a
                      href={twitterShareUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="btn btn-outline"
                    >
                      X共有
                    </a>
                    <a
                      href={lineShareUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="btn btn-outline"
                    >
                      LINE共有
                    </a>
                    <button
                      type="button"
                      className="btn btn-primary"
                      onClick={handleNativeShare}
                    >
                      その他共有
                    </button>
                  </div>
                </div>
              )}
              {currentUser && !isOrganizer && recruitment.status === 'active' && (
                <>
                  {!hasApplied ? (
                    <div className="card" style={{ padding: '14px', marginTop: '12px' }}>
                      <h3 style={{ marginTop: 0 }}>応募前アンケート（任意）</h3>
                      <p className="form-help">未入力でも応募できます。主催者の計画の参考になります。</p>
                      <div className="form-group">
                        <label>不安に感じること（任意・複数選択可）</label>
                        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginTop: '8px' }}>
                          {PRE_HIKE_CONCERN_OPTIONS.map(option => (
                            <label key={option.value} className="checkbox-label" style={{ width: 'auto' }}>
                              <input
                                type="checkbox"
                                checked={preHikeSurvey.concerns.includes(option.value)}
                                onChange={() => handlePreHikeConcernChange(option.value)}
                              />
                              {option.label}
                            </label>
                          ))}
                        </div>
                      </div>
                      <div className="form-group">
                        <label>登山保険（任意）</label>
                        <select
                          className="form-control"
                          value={preHikeSurvey.insuranceStatus}
                          onChange={e => setPreHikeSurvey(prev => ({ ...prev, insuranceStatus: e.target.value }))}
                        >
                          <option value="">回答しない</option>
                          <option value="joined">加入済み</option>
                          <option value="not_joined">未加入</option>
                          <option value="unknown">分からない</option>
                        </select>
                      </div>
                      <div className="form-group">
                        <label>補足（任意）</label>
                        <textarea
                          className="form-control"
                          value={preHikeSurvey.comment}
                          onChange={e => setPreHikeSurvey(prev => ({ ...prev, comment: e.target.value }))}
                          placeholder="不安なことや主催者に伝えたいことがあれば入力してください"
                          rows={3}
                        />
                      </div>
                      {(() => {
                        const cond = recruitment.participantCondition;
                        const genderBlocked = (cond === 'women_only' || cond === 'men_only') &&
                          (cond === 'women_only' ? myGender !== '女性' : myGender !== '男性');
                        return (
                          <>
                            {genderBlocked && (
                              <p style={{ color: 'var(--ember)', fontSize: '0.85rem', marginBottom: '8px' }}>
                                募集条件に性別が含まれる場合、プロフィールの設定が一致する人のみ応募可能です。
                              </p>
                            )}
                            <button
                              className={`btn btn-primary ${(isFull || genderBlocked) ? 'disabled' : ''}`}
                              onClick={handleApply}
                              disabled={isFull || genderBlocked}
                            >
                              {isFull ? '募集終了' : '応募する'}
                            </button>
                          </>
                        );
                      })()}
                    </div>
                  ) : (
                    <button
                      className="btn btn-danger"
                      onClick={handleCancelApplication}
                    >
                      応募をキャンセル
                    </button>
                  )}
                </>
              )}

              {hasApplied && !isOrganizer && (
                <button
                  className="btn btn-secondary"
                  onClick={() => handleStartChat(recruitment.userId, organizer?.nickname)}
                >
                  主催者とチャット
                </button>
              )}

              {isOrganizer && (
                <div className="organizer-actions">
                  <Link to={`/edit-recruitment/${id}`} className="btn btn-outline">
                    編集する
                  </Link>
                  <button className="btn btn-danger" onClick={handleDeleteRecruitment}>
                    募集を削除
                  </button>
                </div>
              )}
            </div>

            {/* 相互評価セクション - 募集終了になった単発募集のみ */}
            {recruitment.recruitmentType === 'single' && (() => {
              if (recruitment.status !== 'closed' || !currentUser) return null;

              return (
                <div className="card review-section">
                  <h3>評価</h3>
                      {!showReviewForm ? (
                    <>
                      {!isOrganizer && myApplication?.applicationStatus === 'approved' && !reviewedTargetIds.has(recruitment.userId) && (
                        <button className="btn btn-outline" onClick={() => {
                          setReviewTarget({ userId: recruitment.userId, nickname: organizer?.nickname || '主催者' });
                          setShowReviewForm(true);
                        }}>主催者を評価する</button>
                      )}
                      {!isOrganizer && reviewedTargetIds.has(recruitment.userId) && (
                        <p className="review-unavailable">主催者への評価は送信済みです。</p>
                      )}
                      {!isOrganizer && hasApplied && myApplication?.applicationStatus !== 'approved' && (
                        <p className="review-unavailable">評価は承認済みの参加者のみ送信できます。</p>
                      )}
                      {isOrganizer && applications.filter(app => app.applicationStatus === 'approved').map(app => (
                        <div key={app.id} className="applicant-review-row">
                          <span>{app.applicant?.nickname}</span>
                          {reviewedTargetIds.has(app.userId) ? (
                            <span className="review-unavailable">評価送信済み</span>
                          ) : (
                            <button className="btn btn-outline btn-sm" onClick={() => {
                              setReviewTarget({ userId: app.userId, nickname: app.applicant?.nickname || '応募者' });
                              setShowReviewForm(true);
                            }}>評価する</button>
                          )}
                        </div>
                      ))}
                    </>
                  ) : (
                    <div className="review-form">
                      <h4>{reviewTarget?.nickname} への評価</h4>
                      <div className="rating-buttons">
                        <button
                          className={`btn ${reviewRating === 'good' ? 'btn-primary' : 'btn-secondary'}`}
                          onClick={() => setReviewRating('good')}
                        >良かった</button>
                        <button
                          className={`btn ${reviewRating === 'bad' ? 'btn-danger' : 'btn-secondary'}`}
                          onClick={() => setReviewRating('bad')}
                        >残念だった</button>
                      </div>
                      <textarea
                        className="form-control"
                        value={reviewComment}
                        onChange={e => setReviewComment(e.target.value)}
                        placeholder="ひとことコメント（任意）"
                        rows={3}
                        style={{marginTop: '12px'}}
                      />
                      <div className="card" style={{ padding: '12px', marginTop: '12px' }}>
                        <h4 style={{ marginTop: 0 }}>山行後アンケート（任意）</h4>
                        <p className="form-help">未入力でも評価を送信できます。サービス改善に利用します。</p>
                        <div className="form-row">
                          <div className="form-group">
                            <label>実際に山行できましたか</label>
                            <select
                              className="form-control"
                              value={postHikeSurvey.actualCompleted}
                              onChange={e => handlePostHikeSurveyChange('actualCompleted', e.target.value)}
                            >
                              <option value="">回答しない</option>
                              <option value="yes">実施できた</option>
                              <option value="no">実施できなかった</option>
                              <option value="partial">一部変更して実施</option>
                            </select>
                          </div>
                          <div className="form-group">
                            <label>満足度</label>
                            <select
                              className="form-control"
                              value={postHikeSurvey.satisfaction}
                              onChange={e => handlePostHikeSurveyChange('satisfaction', e.target.value)}
                            >
                              <option value="">回答しない</option>
                              <option value="5">5 とても満足</option>
                              <option value="4">4 満足</option>
                              <option value="3">3 普通</option>
                              <option value="2">2 やや不満</option>
                              <option value="1">1 不満</option>
                            </select>
                          </div>
                        </div>
                        <div className="form-row">
                          <div className="form-group">
                            <label>装備不足はありましたか</label>
                            <select
                              className="form-control"
                              value={postHikeSurvey.equipmentShortage}
                              onChange={e => handlePostHikeSurveyChange('equipmentShortage', e.target.value)}
                            >
                              <option value="">回答しない</option>
                              <option value="yes">あった</option>
                              <option value="no">なかった</option>
                            </select>
                          </div>
                          <div className="form-group">
                            <label>装備不足カテゴリ（任意）</label>
                            <select
                              className="form-control"
                              value={postHikeSurvey.equipmentShortageCategory}
                              onChange={e => handlePostHikeSurveyChange('equipmentShortageCategory', e.target.value)}
                              disabled={postHikeSurvey.equipmentShortage !== 'yes'}
                            >
                              <option value="">回答しない</option>
                              {POST_HIKE_EQUIPMENT_SHORTAGE_OPTIONS.map(option => (
                                <option key={option.value} value={option.value}>{option.label}</option>
                              ))}
                            </select>
                          </div>
                        </div>
                        <div className="form-row">
                          <div className="form-group">
                            <label>下山後の立ち寄りカテゴリ（任意）</label>
                            <select
                              className="form-control"
                              value={postHikeSurvey.stopoverCategory}
                              onChange={e => handlePostHikeSurveyChange('stopoverCategory', e.target.value)}
                            >
                              <option value="">回答しない</option>
                              {POST_HIKE_STOPOVER_OPTIONS.map(option => (
                                <option key={option.value} value={option.value}>{option.label}</option>
                              ))}
                            </select>
                          </div>
                          <div className="form-group">
                            <label>下山後の立ち寄り（任意）</label>
                            <input
                              className="form-control"
                              value={postHikeSurvey.stopover}
                              onChange={e => handlePostHikeSurveyChange('stopover', e.target.value)}
                              placeholder="例: 温泉、食事、道の駅"
                            />
                          </div>
                        </div>
                        <div className="form-row">
                          <div className="form-group">
                            <label>次に行きたい山域（任意）</label>
                            <select
                              className="form-control"
                              value={postHikeSurvey.nextMountainArea}
                              onChange={e => handlePostHikeSurveyChange('nextMountainArea', e.target.value)}
                            >
                              <option value="">回答しない</option>
                              {NEXT_MOUNTAIN_AREA_OPTIONS.map(option => (
                                <option key={option.value} value={option.value}>{option.label}</option>
                              ))}
                            </select>
                          </div>
                          <div className="form-group">
                            <label>次に行きたい山（任意）</label>
                            <input
                              className="form-control"
                              value={postHikeSurvey.nextMountain}
                              onChange={e => handlePostHikeSurveyChange('nextMountain', e.target.value)}
                              placeholder="例: 北岳"
                            />
                          </div>
                        </div>
                        <div className="form-group">
                          <label>補足（任意）</label>
                          <textarea
                            className="form-control"
                            value={postHikeSurvey.comment}
                            onChange={e => handlePostHikeSurveyChange('comment', e.target.value)}
                            placeholder="想定よりきつかった点、役立った装備など"
                            rows={3}
                          />
                        </div>
                      </div>
                      <div style={{display:'flex', gap:'8px', marginTop:'12px'}}>
                        <button className="btn btn-primary" onClick={handleSubmitReview}>評価を送信</button>
                        <button className="btn btn-secondary" onClick={() => {
                          setShowReviewForm(false);
                          setReviewTarget(null);
                          setPostHikeSurvey(POST_HIKE_INITIAL);
                        }}>キャンセル</button>
                      </div>
                    </div>
                  )}
                </div>
              );
            })()}
          </div>
        </div>

        <div className="sidebar">
          <div className="card organizer-info">
            <h3>募集者情報</h3>
            {organizer && (
              <div className="organizer-details">
                <p className="nickname">
                  <Link to={`/users/${recruitment.userId}`} style={{ color: 'var(--alpine)', textDecoration: 'underline', cursor: 'pointer' }}>
                    {organizer.nickname}
                  </Link>
                </p>
                {organizer.bio && (
                  <p className="bio">{toPlainText(organizer.bio)}</p>
                )}
                {organizer.climbingHistory && (
                  <div className="history">
                    <label>登山歴</label>
                    <p>{toPlainText(organizer.climbingHistory)}</p>
                  </div>
                )}
                {organizer.favoriteMountains && (
                  <div className="favorites">
                    <label>好きな山</label>
                    <p>{toPlainText(organizer.favoriteMountains)}</p>
                  </div>
                )}
              </div>
            )}
          </div>

          <div className="card applicants-info">
            <h3>応募者一覧</h3>
            {isOrganizer && applications.length > 0 ? (
              <div className="applicants-list">
                {applications.map((application) => {
                  const appStatus = application.applicationStatus || 'pending';
                  const statusLabel = { pending: '承認待ち', approved: '承認済み', rejected: '拒否済み' }[appStatus];
                  const statusClass = { pending: 'status-pending', approved: 'status-approved', rejected: 'status-rejected' }[appStatus];
                  return (
                    <div key={application.id} className="applicant-item">
                      <div className="applicant-top">
                        <p className="applicant-name">
                          <Link to={`/users/${application.userId}`} style={{ color: 'var(--alpine)', textDecoration: 'underline' }}>
                            {getApplicantDisplayName(application)}
                          </Link>
                        </p>
                        <span className={`app-status-badge ${statusClass}`}>{statusLabel}</span>
                      </div>
                      <p className="apply-date">
                        応募日: {application.createdAt?.toDate ? application.createdAt.toDate().toLocaleDateString() : new Date(application.createdAt).toLocaleDateString()}
                      </p>
                      {(application.concerns?.length > 0 || application.insuranceStatus || application.comment) && (
                        <div className="applicant-survey">
                          {application.concerns?.length > 0 && (
                            <p className="survey-row"><span>不安:</span> {application.concerns.map(v => PRE_HIKE_CONCERN_OPTIONS.find(o => o.value === v)?.label || v).join('、')}</p>
                          )}
                          {application.insuranceStatus && (
                            <p className="survey-row"><span>登山保険:</span> {{ joined: '加入済み', not_joined: '未加入', unknown: '分からない' }[application.insuranceStatus] || application.insuranceStatus}</p>
                          )}
                          {application.comment && (
                            <p className="survey-row"><span>補足:</span> {application.comment}</p>
                          )}
                        </div>
                      )}
                      <div className="applicant-actions">
                        <button
                          className="btn btn-outline"
                          style={{fontSize:'12px', padding:'4px 8px'}}
                          onClick={() => handleStartChat(application.userId, getApplicantDisplayName(application))}
                        >
                          チャット
                        </button>
                        {appStatus === 'pending' && (
                          <>
                            <button
                              className="btn btn-primary"
                              style={{fontSize:'12px', padding:'4px 8px'}}
                              onClick={() => handleApproveApplication(application.id)}
                              disabled={approvingIds.has(application.id)}
                            >
                              {approvingIds.has(application.id) ? '処理中...' : '承認'}
                            </button>
                            <button
                              className="btn btn-danger"
                              style={{fontSize:'12px', padding:'4px 8px'}}
                              onClick={() => handleRejectApplication(application.id)}
                            >
                              拒否
                            </button>
                          </>
                        )}
                        {appStatus === 'approved' && (
                          <button
                            className="btn btn-outline"
                            style={{fontSize:'12px', padding:'4px 8px'}}
                            onClick={() => handleUnapproveApplication(application.id)}
                          >
                            承認解除
                          </button>
                        )}
                        {appStatus === 'rejected' && (
                          <button
                            className="btn btn-outline"
                            style={{fontSize:'12px', padding:'4px 8px'}}
                            onClick={() => handleUnrejectApplication(application.id)}
                          >
                            拒否解除
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : !isOrganizer && recruitment.applicationCount > 0 ? (
              <p className="applicants-count">応募者数: {recruitment.applicationCount}人</p>
            ) : (
              <p className="no-applicants">まだ応募者がいません</p>
            )}

            {/* グループチャット開始ボタン（主催者のみ） */}
            {isOrganizer && applications.some(a => a.applicationStatus === 'approved') && (
              <div className="group-chat-start-section">
                <button
                  className="btn btn-primary"
                  onClick={handleStartGroupChat}
                  style={{ marginTop: '12px' }}
                >
                  💬 グループチャット開始（承認済み全員）
                </button>
                <p className="form-help">承認済みの全参加者が入るグループチャットルームを作成・開始します</p>
              </div>
            )}
          </div>

          {currentUser && !isOrganizer && (
            <div className="card report-section">
              {!showReportForm ? (
                <button
                  className="btn btn-link report-btn"
                  onClick={() => setShowReportForm(true)}
                >
                  この募集を通報する
                </button>
              ) : (
                <div>
                  <h4 style={{color:'var(--dark-gray)', marginBottom:'12px'}}>通報</h4>
                  <div className="form-group" style={{marginBottom:'10px'}}>
                    <select
                      className="form-control"
                      value={reportReason}
                      onChange={e => setReportReason(e.target.value)}
                      style={{fontSize:'0.9rem'}}
                    >
                      <option value="">理由を選択</option>
                      {REPORT_REASON_OPTIONS.map(option => (
                        <option key={option.value} value={option.value}>{option.label}</option>
                      ))}
                    </select>
                  </div>
                  <textarea
                    className="form-control"
                    value={reportComment}
                    onChange={e => setReportComment(e.target.value)}
                    placeholder="詳細（任意）"
                    rows={2}
                    style={{fontSize:'0.85rem', marginBottom:'10px'}}
                  />
                  <div style={{display:'flex',gap:'8px'}}>
                    <button className="btn btn-danger" style={{fontSize:'0.85rem',padding:'6px 14px'}} onClick={handleReport}>通報する</button>
                    <button className="btn btn-secondary" style={{fontSize:'0.85rem',padding:'6px 14px'}} onClick={() => setShowReportForm(false)}>キャンセル</button>
                  </div>
                </div>
              )}
            </div>
          )}

          <div className="card actions">
            {isOrganizer && (
              <Link to={`/edit-recruitment/${id}`} className="btn btn-outline">
                募集を編集
              </Link>
            )}

            <Link to="/recruitments" className="btn btn-primary">
              募集一覧に戻る
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}

export default RecruitmentDetail;
